import type { ReactNode } from "react";

export type FaqItem = {
  q: string;
  a: ReactNode;
};

export const FAQS: FaqItem[] = [
  {
    q: "How much will I get paid?",
    a: (
      <>
        <p>
          You will get paid based on the number of devices you host. The payment schedule is as
          follows:
        </p>
        <ul>
          <li>Up to 80 Device Kits: $200/month</li>
          <li>81–160 Device Kits: $400/month</li>
          <li>161–320 Device Kits: $800/month</li>
          <li>321–400 Device Kits: $1,600/month</li>
        </ul>
      </>
    ),
  },
  {
    q: "What is a Proxidize kit?",
    a: (
      <p>
        A Proxidize kit is a collection of devices made up mostly of one or more central computer,
        one or more USB hubs, and phones along with their SIM cards.
      </p>
    ),
  },
  {
    q: "Is the kit harmful in any way?",
    a: (
      <p>
        No, all Proxidize equipment is 100% safe and FCC-certified. We conduct rigorous testing in
        professional labs to ensure your safety.
      </p>
    ),
  },
  {
    q: "How much space does this need?",
    a: (
      <p>
        We will be sending either 20, 40, 80, 160, or 320 device kits, all of which need about 10–30
        sq. ft. or less.
      </p>
    ),
  },
  {
    q: "What technical knowledge do I need to have?",
    a: (
      <p>
        You don&apos;t need any technical knowledge or experience whatsoever. If you know how to
        turn on a computer, you&apos;ll be able to join!
      </p>
    ),
  },
  {
    q: "How and when will I be paid?",
    a: (
      <p>
        Once everything is set up, you&apos;ll be paid on the 7th of every month in advance via wire
        transfer, with your first payment being prorated.
      </p>
    ),
  },
  {
    q: "Do I need to pay for anything?",
    a: (
      <p>
        You don&apos;t need to pay for anything. We&apos;ll cover everything you need, including an
        extension cord.
      </p>
    ),
  },
  {
    q: "What legal liabilities do I bear when hosting the kit?",
    a: (
      <p>
        You bear no legal liabilities; we take full legal responsibility for ensuring the kit is
        used in a legal, ethical, and responsible manner.
      </p>
    ),
  },
  {
    q: "How does Proxidize ensure my privacy and security?",
    a: (
      <ul>
        <li>
          We take extreme measures to ensure your privacy and security. For starters, none of your
          information will ever be shared with anyone, and your IP address will be protected behind
          our internal Proxidize VPN.
        </li>
        <li>
          Your Proxidize kit will never be able to access any other devices on your network and you
          can rest assured that all your data will be safe and secure.
        </li>
        <li>
          If you have any specific questions or concerns, please don&apos;t hesitate to contact us!
        </li>
      </ul>
    ),
  },
  {
    q: "What happens if the kit stops working or if I encounter technical issues?",
    a: (
      <p>
        Our team is available to help with anything around the clock. In the rare case that we need
        anything, we&apos;ll send you an email with clear instructions. However, we have redundancy
        measures in place to ensure this will rarely happen.
      </p>
    ),
  },
  {
    q: "Do I need to be available 24/7 around the kit?",
    a: (
      <p>
        Not at all. Once fully set up, you will be able to forget about the kit. In the rare cases
        where we might need your help, we will pay you for your time if you spend more than one hour
        of your time in a given month. If we impose on your time for more than an hour, we will
        compensate you $50 for every 30 minutes you spend helping us. We don&apos;t expect this to
        happen, as we will provide you with an automated power extension cord that can automatically
        power cycle the hardware if needed.
      </p>
    ),
  },
  {
    q: "Can I move the kit to another location or must it stay in one place?",
    a: (
      <p>
        Once a location is set up, we expect the kit to remain in the same place. However, if you
        need to move the kit, please let us know 30 days in advance.
      </p>
    ),
  },
  {
    q: "What steps are involved in the application process, and how long does it take to get approved?",
    a: (
      <ul>
        <li>Submit the initial application by filling out the form.</li>
        <li>Once reviewed, we might call you to ask about a few things.</li>
        <li>
          Once your initial application is approved, we&apos;ll send you our legal form to fill out
          and provide your ID, proof of address, an emergency contact, and a few other details.
        </li>
        <li>We will then send you our contract to sign via DocuSign.</li>
        <li>We will then send you the kit.</li>
        <li>Once the kit is delivered, we&apos;ll help you set it up.</li>
        <li>
          We will collect your payment information for wire transfer, and send you your first
          payment.
        </li>
        <li>You will then start receiving the monthly payment on the 7th of each month.</li>
      </ul>
    ),
  },
  {
    q: "Can I host more than one kit for additional income?",
    a: (
      <p>
        We only need one or two kits per city. So if you have another location in a different city,
        please contact us about it and we might be able to have you join under that location as
        well!
      </p>
    ),
  },
  {
    q: "What is the duration of the hosting agreement? Can I opt out?",
    a: (
      <p>
        The program term is 24 months, and you can cancel at any time without penalty by providing
        us with a 30-day notice. Furthermore, the first month is considered a probationary period,
        during which you can cancel at any time without notice.
      </p>
    ),
  },
  {
    q: "How does the referral program work, and how do I refer someone?",
    a: (
      <>
        <p>
          If you know someone eligible, you can refer them to the program by having them fill out
          the initial form and using your referral link. If they join successfully, we will pay you
          $200.00 via wire transfer after 30 days of onboarding them.
        </p>
        <p>
          You can create your own referring link using the template:{" "}
          <code className="break-all">
            https://proxidize.com/proxidize-hosting-program?referral=YOUR_EMAIL
          </code>
        </p>
      </>
    ),
  },
  {
    q: "Will this affect my internet speed or connection?",
    a: (
      <p>
        Assuming your connection is faster than 100 Mbps, then no, the kit will not have any effect
        on your internet speed or connection.
      </p>
    ),
  },
  {
    q: "What if I don’t want to be paid via wire transfer?",
    a: <p>We can also just give you Proxidize credit!</p>,
  },
  {
    q: "Will this affect the quality of my IP?",
    a: (
      <p>
        Not at all! For starters, we will never use your main internet IP for any purpose except
        connecting to the kit. Mobile IPs are powered by CGNAT and as such nothing will ever affect
        their quality.
      </p>
    ),
  },
];
